'use client';

import { type ReactNode } from 'react';

import { cn } from '@ps/ui/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowLeft } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: string;
  /**
   * Renders below the title; used by the Members / Reports / etc.
   * panels to surface tab strips, action buttons, etc.
   */
  headerExtra?: ReactNode;
  children?: ReactNode;
  /**
   * Optional max-width override. Defaults to the design's ~460px
   * side-pane width that matches the thread pane.
   */
  maxWidthClassName?: string;
}

/**
 * Side-aligned overlay panel for the channel info sub-views
 * (Members / Reports / Bookmarks). The visual story matches the
 * thread side pane: 460px-wide column on desktop, full-screen on
 * mobile, with a "back" arrow that closes the panel and returns
 * the user to the channel info popover.
 *
 * Wraps Radix Dialog directly rather than the shadcn `Sheet` atom
 * because the project doesn't have `Sheet` installed yet and the
 * shadcn CLI is currently flaky in this environment.
 */
export function ChannelPanelSheet({
  open,
  onOpenChange,
  title,
  headerExtra,
  children,
  maxWidthClassName = 'lg:max-w-[460px]',
}: Props) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 bg-scrim/60 fixed inset-0 z-50" />
        <DialogPrimitive.Content
          className={cn(
            // Slide in from the right on desktop, full-screen takeover on mobile.
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            'text-content-1 bg-surface-1 fixed inset-0 z-50 flex flex-col shadow-xl outline-none',
            'lg:left-auto lg:right-0 lg:h-full lg:w-full',
            maxWidthClassName,
          )}
        >
          <header className="border-line flex shrink-0 items-center gap-3 border-b px-4 py-4">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Back"
              className="hover:text-content-1 text-content-2"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </button>
            <DialogPrimitive.Title className="text-content-1 flex-1 text-center text-base font-semibold">
              {title}
            </DialogPrimitive.Title>
            <span className="w-5" aria-hidden />
          </header>
          {headerExtra ? (
            <div className="shrink-0 px-4 pt-3">{headerExtra}</div>
          ) : null}
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
